import React, { useCallback, useEffect, useRef, useState } from 'react';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import DOMPurify from 'isomorphic-dompurify';
import styled from 'styled-components';

const FormWrapper = styled.div`
  margin-top: 1rem;

  .cds--inline-notification,
  .cds--actionable-notification {
    max-width: 100%;
    margin: 1rem 0;
  }
`;

const ButtonRow = styled.div`
  margin-top: 1.5rem;
  display: flex;

  button {
    max-width: 100%;
    justify-content: center;
  }
`;

const ResendLink = styled.button`
  display: block;
  background: none;
  border: none;
  color: var(--cds-link-01, #0f62fe);
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-top: 1rem;
  text-decoration: underline;

  &:hover {
    color: var(--cds-link-02, #0043ce);
  }

  &:disabled {
    color: var(--cds-text-disabled, #c6c6c6);
    cursor: not-allowed;
  }
`;

interface WorkOSCodeFormProps {
  email: string;
  errorMessage: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBackToEmail: () => void;
  processing: boolean;
}

const CODE_LENGTH = 6;
const CODE_REGEX = /^\d{6}$/;
const RESEND_COOLDOWN_SECONDS = 60;

const ERROR_CONFIG: Record<string, { html?: boolean }> = {
  invalidCode: {},
  noAccount: {},
};

const WorkOSCodeForm: React.FC<WorkOSCodeFormProps> = ({
  email,
  errorMessage,
  onSubmit,
  onResend,
  onBackToEmail,
  processing,
}) => {
  const [code, setCode] = useState('');
  const [showTimeoutHint, setShowTimeoutHint] = useState(false);
  const [resendKey, setResendKey] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(
    RESEND_COOLDOWN_SECONDS,
  );
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setShowTimeoutHint(false);
    const timer = setTimeout(() => setShowTimeoutHint(true), 60_000);
    return () => clearTimeout(timer);
  }, [resendKey]);

  useEffect(() => {
    startCooldown();
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCooldown = useCallback(() => {
    setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1_000);
  }, []);

  const isValidCode = CODE_REGEX.test(code);
  const resendOnCooldown = cooldownRemaining > 0;
  const errorConfig = errorMessage ? ERROR_CONFIG[errorMessage] : null;
  const translatedError = errorMessage
    ? i18n.getStr(
        `components.WorkOSLogin.${errorConfig ? errorMessage : 'genericError'}`,
      )
    : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidCode) {
      onSubmit(code);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(value);
  };

  const handleResend = () => {
    if (resendOnCooldown) return;
    setResendKey(k => k + 1);
    startCooldown();
    onResend();
  };

  return (
    <FormWrapper data-testid='workos-code-form'>
      <h3>{i18n.getStr('components.WorkOSLogin.codeTitle')}</h3>
      <p>
        {i18n
          .getStr('components.WorkOSLogin.codeSubtitle')
          .replace('{email}', email)}
      </p>

      {translatedError &&
        (errorConfig?.html ? (
          <M.ActionableNotification
            kind='error'
            title=''
            subtitle={
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(translatedError, {
                    ADD_ATTR: ['target'],
                  }),
                }}
              />
            }
            hideCloseButton
            data-testid='workos-code-error'
          />
        ) : (
          <M.InlineNotification
            kind='error'
            title={translatedError}
            hideCloseButton
            data-testid='workos-code-error'
          />
        ))}

      {showTimeoutHint && !errorMessage && !processing && (
        <M.InlineNotification
          kind='info'
          title={i18n.getStr('components.WorkOSLogin.codeTimeoutHint')}
          hideCloseButton
          data-testid='workos-timeout-hint'
        />
      )}

      <form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
        <M.TextInput
          id='workos-code-input'
          data-testid='workos-code-input'
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          labelText={i18n.getStr('components.WorkOSLogin.codeLabel')}
          placeholder={i18n.getStr('components.WorkOSLogin.codePlaceholder')}
          value={code}
          onChange={handleCodeChange}
          disabled={processing}
          maxLength={CODE_LENGTH}
        />
        <ButtonRow>
          <M.Button
            type='submit'
            kind='primary'
            data-testid='workos-verify-button'
            disabled={processing || !isValidCode}
            style={{ width: '100%' }}
          >
            {processing ? (
              <M.LoadingInline className='button-loading-icon' />
            ) : (
              i18n.getStr('components.WorkOSLogin.verifyButton')
            )}
          </M.Button>
        </ButtonRow>
      </form>

      <ResendLink
        type='button'
        onClick={handleResend}
        disabled={processing || resendOnCooldown}
        data-testid='workos-resend-code'
      >
        {resendOnCooldown
          ? i18n
              .getStr('components.WorkOSLogin.resendCooldown')
              .replace('{seconds}', String(cooldownRemaining))
          : i18n.getStr('components.WorkOSLogin.resendCode')}
      </ResendLink>
      <ResendLink
        type='button'
        onClick={onBackToEmail}
        disabled={processing}
        data-testid='workos-back-to-email'
      >
        {i18n.getStr('components.WorkOSLogin.useDifferentEmail')}
      </ResendLink>
    </FormWrapper>
  );
};

export default i18n.renderTranslation()(WorkOSCodeForm);
